import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Tailwind class extras para el contenedor scrolleable interno. */
  className?: string;
}

/**
 * Fila scrolleable horizontalmente que muestra un chevron animado
 * SOLO cuando hay overflow real:
 *   - Al inicio (o si no se ha hecho scroll): chevron derecho si hay
 *     más contenido a la derecha
 *   - Al final: chevron izquierdo si hay más contenido a la izquierda
 *   - En medio: ambos chevrones
 *   - Si todo cabe en pantalla: ninguno
 *
 * Las flechas son pointer-events-none para no bloquear el swipe del
 * usuario. Recalcula en mount + scroll + resize del viewport.
 */
export function ScrollHintRow({ children, className = '' }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hint, setHint] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => {
      // Tolerancia de 2px para evitar parpadeos en bordes exactos
      // (algunos navegadores reportan scrollLeft con decimales).
      const left = el.scrollLeft > 2;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setHint(prev => (prev.left === left && prev.right === right) ? prev : { left, right });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    // ResizeObserver para detectar cuando el contenido cambia (ej.
    // botones condicionales que aparecen/desaparecen según rol).
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      // También observar al primer hijo (el flex container) por si
      // su scrollWidth cambia sin cambiar el del scroller.
      const inner = el.firstElementChild;
      if (inner) ro.observe(inner);
    }

    window.addEventListener('resize', update);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [children]);

  return (
    <div className="relative">
      <div ref={scrollerRef} className={`overflow-x-auto no-scrollbar ${className}`}>
        {children}
      </div>
      {hint.left && (
        <div aria-hidden className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 flex items-center pr-4 pl-2 bg-gradient-to-r from-background via-background/90 to-transparent">
          <ChevronLeft className="w-4 h-4 text-accent animate-pulse" />
        </div>
      )}
      {hint.right && (
        <div aria-hidden className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 flex items-center pl-4 pr-2 bg-gradient-to-l from-background via-background/90 to-transparent">
          <ChevronRight className="w-4 h-4 text-accent animate-pulse" />
        </div>
      )}
    </div>
  );
}
